import React, { useState } from "react"
import { Input } from "antd"
import { useNavigate } from "react-router-dom"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { ROUTES } from "constants/routes"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { useConsoleHandoffStore } from "features/QueryConsole/consoleHandoff"
import { isReservedQueryFileName, ScriptMeta } from "types/savedQuery.types"
import { errMsgOf, failureOf } from "utils/errMsgOf"
import {
  ProposalStatus,
  selectConversation,
  useAssistantStore,
} from "../assistantStore"
import { useAssistantScreen } from "../screenRegistry"
import type { ProposalOf } from "../types"

/** The prefix of the scripts the MCP server saves; the converter refuses it too. */
export const MCP_SCRIPT_PREFIX = "MCP - "

/** Characters a saved query's name cannot hold (the file system's, and those that break its URL). */
const BAD_NAME_CHARS = /[\\/:*?"<>|&#%]/

export const NAME_EMPTY_TEXT = "Enter a name for the script."
export const MCP_NAME_TEXT = `Names starting with "${MCP_SCRIPT_PREFIX}" are kept for the scripts AI agents save. Choose another name.`
export const RESERVED_NAME_TEXT =
  "That name is kept for the platform's own files. Choose another name."
export const NAME_CHARS_TEXT = 'A name cannot contain \\ / : * ? " < > | & # %'

export const nameTakenText = (name: string): string =>
  `There is already a saved query called "${name}". Choose another name.`

export const saveFailedText = (message: string): string =>
  message ? `The script could not be saved: ${message}` : "The script could not be saved."

/** Why `name` cannot be a new saved script's name, or null. */
export const scriptNameProblem = (name: string): string | null => {
  const trimmed = name.trim()
  if (trimmed === "") return NAME_EMPTY_TEXT
  if (trimmed.toLowerCase().startsWith(MCP_SCRIPT_PREFIX.toLowerCase())) return MCP_NAME_TEXT
  if (isReservedQueryFileName(trimmed.toLowerCase())) return RESERVED_NAME_TEXT
  if (BAD_NAME_CHARS.test(trimmed)) return NAME_CHARS_TEXT
  return null
}

const SCRIPT_META: ScriptMeta = { kind: "script" }

/** Saved without overwrite: Repos refuses a name that is taken. */
const NO_OVERWRITE = ""

/**
 * The saved query in `names` that has `name` in any case (Repos keeps them
 * as files on a file system that ignores case), or undefined.
 */
const takenAs = (names: string[] | null, name: string): string | undefined =>
  names?.find((n) => n.toLowerCase() === name.toLowerCase())

/**
 * The script part of a script proposal's card: the script, a name field
 * (the proposed name to start with) and "Save as script", which saves it as
 * a plain saved query through the user's own call, never over another one.
 * A name taken says so and asks for another. A saved proposal is accepted,
 * and the saved queries are read again (useSavedQueries' save does that),
 * so a tables proposal waiting on the script can be reviewed.
 * "Open in Console" puts the script in the Query Console's editor.
 * A script that failed its check can still be saved: the user decides.
 */
const ScriptProposalCard: React.FC<{
  proposal: ProposalOf<"script">
  status: ProposalStatus
  savedAs?: string
}> = ({ proposal, status, savedAs }) => {
  const { projectId, owner, registration } = useAssistantScreen()
  const setStatus = useAssistantStore((s) => s.setProposalStatus)
  const { getProjectQueries, saveStructured } = useSavedQueries()
  const navigate = useNavigate()
  const [name, setName] = useState(proposal.name)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // A tables proposal waiting on this script finds it only under this name.
  const neededByTables = useAssistantStore((s) =>
    selectConversation(projectId)(s).proposals.some(
      ({ proposal: q, status: st }) =>
        st === "pending" && q.kind === "tabspec" && q.requiresScript === proposal.name
    )
  )

  /** The project's saved query names, or null when they could not be read. */
  const readNames = async (): Promise<string[] | null> => {
    try {
      const res = await getProjectQueries(projectId, owner)
      if (typeof res.data !== "string" || errMsgOf(res.data) !== null) return null
      return res.data
        .split("\n")
        .map((n) => n.trim())
        .filter(Boolean)
    } catch {
      return null
    }
  }

  const save = async (): Promise<void> => {
    const problem = scriptNameProblem(name)
    if (problem) {
      setError(problem)
      return
    }
    const wanted = name.trim()
    setSaving(true)
    setError(null)
    try {
      // Names that can't be read leave the check to Repos, which refuses a taken one.
      const taken = takenAs(await readNames(), wanted)
      if (taken !== undefined) {
        setError(nameTakenText(taken))
        return
      }
      await saveStructured(projectId, owner, wanted, proposal.script, SCRIPT_META, NO_OVERWRITE)
      setStatus(projectId, proposal.id, "accepted", wanted)
    } catch (e) {
      // Repos's refusal does not say why: a name taken since the check is the likely one.
      const taken = takenAs(await readNames(), wanted)
      setError(taken !== undefined ? nameTakenText(taken) : saveFailedText(failureOf(e)))
    } finally {
      setSaving(false)
    }
  }

  const openInConsole = (): void => {
    useConsoleHandoffStore
      .getState()
      .handOff({ projectId, script: proposal.script, name: savedAs ?? null })
    if (registration?.screen !== "console") {
      navigate(`${ROUTES.PROJECT(projectId)}?tab=${PROJECT_TAB_KEYS.DATA_REPORTING}`)
    }
  }

  const pending = status === "pending"

  return (
    <div className="flex flex-col gap-1 pt-1">
      <pre
        className="m-0 max-h-48 overflow-auto rounded bg-gray-50 p-2 text-xs whitespace-pre"
        aria-label="Script"
      >
        {proposal.script}
      </pre>
      {pending && (
        <>
          <Input
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setError(null)
            }}
            onPressEnter={() => void save()}
            disabled={saving}
            aria-label="Script name"
            status={error ? "error" : undefined}
          />
          {error && (
            <div className="text-xs text-red-600" role="alert">
              {error}
            </div>
          )}
          {!error && neededByTables && name.trim() !== proposal.name && (
            <div className="text-xs text-gray-500">
              The tables proposal needs it saved as &quot;{proposal.name}&quot;.
            </div>
          )}
        </>
      )}
      {status === "accepted" && savedAs !== undefined && (
        <div className="text-xs text-green-700">Saved as &quot;{savedAs}&quot;.</div>
      )}
      <div className="flex items-center gap-2 pt-1">
        {pending && (
          <>
            <DataQButton type="primary" size="small" isLoading={saving} onClick={() => void save()}>
              Save as script
            </DataQButton>
            <DataQButton
              type="secondary"
              size="small"
              isDisabled={saving}
              onClick={() => setStatus(projectId, proposal.id, "rejected")}
            >
              Reject
            </DataQButton>
          </>
        )}
        {status !== "rejected" && (
          <DataQButton type="link" size="small" onClick={openInConsole}>
            Open in Console
          </DataQButton>
        )}
      </div>
    </div>
  )
}

export default ScriptProposalCard
